// Qué gastos son "del mes". Regla del negocio (Fefo, 27-oct-2026): los gastos
// comunes se reparten por FECHA DE PAGO. Lo pagado en octubre entra en los
// gastos comunes de octubre, que los residentes pagan en noviembre.
//
// - Pantalla Gastos: los pagados se ubican por fecha de pago (ventana de corte
//   del edificio si tiene día de corte, si no el mes calendario) y los
//   pendientes se ven siempre, hasta que se paguen.
// - Informe de gastos: lo que entra en el cierre de ese mes. Si el cierre ya
//   existe, su lista exacta (liquidatedExpenseIds); si no, la misma selección
//   que haría el cierre (selectLiquidableExpenses), como vista preliminar.

import { isoOf, isInWindow, resolveCierreWindow, normalizeCutoffDay, selectLiquidableExpenses, gastosYaLiquidados } from "./cierrePeriod";

const enMes = (fecha, year, month) => {
  const iso = isoOf(fecha);
  return Boolean(iso) && iso.slice(0, 7) === `${year}-${String(month).padStart(2, "0")}`;
};

/** ¿La fecha cae en el período (ventana de corte si hay día de corte, si no el mes calendario)? */
export function enPeriodo(fecha, { year, month, diaCorte = null }) {
  if (normalizeCutoffDay(diaCorte)) {
    const { start, end } = resolveCierreWindow(month, year, { diaCorte });
    return isInWindow(fecha, start, end);
  }
  return enMes(fecha, year, month);
}

/**
 * Gastos de la pantalla Gastos para un mes: pagados por fecha de pago y todos
 * los pendientes. En la vista de eliminados, por fecha de eliminación.
 */
export function filtrarGastosDelMes(items, { year, month, diaCorte = null, eliminados = false }) {
  return (items || []).filter((g) => {
    if (eliminados) return enMes(g.deletedAt, year, month);
    if (!g.paid) return true;
    return enPeriodo(g.paidDate, { year, month, diaCorte });
  });
}

/** Id del doc de cierre de un mes (montlyaccount). */
export const idCierre = (buildingId, year, month) => `${Number(buildingId)}_${year}_${month}`;

/**
 * Gastos que entran en los gastos comunes de un mes (informe).
 *
 * @param {{ expenses: Array, cierres: Array<{id, state?, liquidatedExpenseIds?, periodStart?, periodEnd?}>,
 *           filasAplicadas: Array<{montlyaccountId?, expenseId?}>, buildingId, year, month, diaCorte? }} p
 * @returns {{ gastos: Array, fuente: "cierre" | "preliminar", cierre: object | null }}
 */
export function gastosDelInforme({ expenses, cierres = [], filasAplicadas = [], buildingId, year, month, diaCorte = null }) {
  const id = idCierre(buildingId, year, month);
  const cierre = cierres.find((c) => c.id === id && c.state !== "processing" && c.state !== "error") || null;

  if (cierre) {
    // Cierre hecho: su lista exacta. Los viejos (sin liquidatedExpenseIds) se
    // reconstruyen con las filas de reparto que dejó ese cierre.
    const ids = Array.isArray(cierre.liquidatedExpenseIds)
      ? new Set(cierre.liquidatedExpenseIds)
      : new Set(filasAplicadas.filter((f) => f.montlyaccountId === id).map((f) => f.expenseId));
    return { gastos: (expenses || []).filter((e) => ids.has(e.id)), fuente: "cierre", cierre };
  }

  const { end } = resolveCierreWindow(month, year, { diaCorte });
  const appliedExpenseIds = gastosYaLiquidados({ cierres, filasAplicadas, excluirCierreId: id });
  return {
    gastos: selectLiquidableExpenses(expenses, { appliedExpenseIds, endDate: end }),
    fuente: "preliminar",
    cierre: null,
  };
}
